import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { useStreamedText, sliceStream } from '../hooks/useStreamedText';
import { smoothScrollToElement } from '../utils/smoothScroll';
import {
  askBazi, BaziChatMessage, BaziReadingData
} from '../services/baziAiService';

/* ============================================================
   八字 AI 求测与解读（集成组件，位于命盘面板之下）
   - 「大师解读」：八小节完整批命
   - 自由提问 / 追问：chart + 全部历史消息每次随请求发送（服务端固化）
   - 双层缓存：Firestore 会话（服务端自动写入）＋ 本地 localStorage 按命盘
     身份（solarText|gender|name|city）缓存，切页返回/重进不丢上下文
   ============================================================ */

const CACHE_KEY = 'bz-ai-cache-v2';
const CACHE_LIMIT = 6;

const READING_HINTS = [
  '我适合走哪种职业方向？',
  '最近两年财运怎么样？',
  '说说我的婚姻感情。',
  '我身体上要注意什么？'
];

interface BaziAiCacheEntry {
  identity: string;
  sessionId: string | null;
  title: string;
  messages: BaziChatMessage[];
  updatedAt: number;
}

function loadAllCache(): BaziAiCacheEntry[] {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list.filter(e => e && typeof e.identity === 'string' && Array.isArray(e.messages)) : [];
  } catch {
    return [];
  }
}

function saveCache(entry: BaziAiCacheEntry): void {
  try {
    const list = loadAllCache().filter(e => e.identity !== entry.identity);
    list.unshift(entry);
    localStorage.setItem(CACHE_KEY, JSON.stringify(list.slice(0, CACHE_LIMIT)));
  } catch {
    /* 忽略配额类错误 */
  }
}

function chartIdentity(chart: any): string {
  if (!chart) return '';
  const input = chart.input || {};
  return [chart.solarText || '', input.gender || '', input.name || '', input.city || ''].join('|');
}

/* ---------- 渲染辅助：文本行（白话一句话行高亮） ---------- */
function BaziTextLines({ text, caret = false }: { text: string; caret?: boolean }) {
  const lines = String(text || '').split('\n').filter(l => l.trim().length > 0);
  return (
    <>
      {lines.map((line, i) => (
        /^白话一句话[:：]/.test(line.trim())
          ? <div key={i} className="bz-ai-bubble-takeaway">
              {line}
              {caret && i === lines.length - 1 && <span className="stream-caret" aria-hidden />}
            </div>
          : <p key={i}>
              {line}
              {caret && i === lines.length - 1 && <span className="stream-caret" aria-hidden />}
            </p>
      ))}
    </>
  );
}

function stripHeader(text: string): string {
  return String(text || '').replace(/^【[^】]{1,12}】\s*/, '').trim();
}

/* ---------- 问答「做／避」前缀拆分 ---------- */
function splitAdviceTag(text: string): { tag: string; text: string } {
  const raw = String(text || '').trim();
  const m = raw.match(/^([做避宜忌禁])[:：]\s*/);
  return m ? { tag: m[1], text: raw.slice(m[0].length) } : { tag: '', text: raw };
}

/* ---------- 尾部公共件：免责声明 + 跳过按钮 ---------- */
const CommonTail: React.FC<{ data: BaziReadingData; stream: any; animate: boolean }> = ({ data, stream, animate }) => (
  <>
    {data.disclaimer && (!animate || stream.done) && (
      <div className="bz-ai-disclaimer">{stripHeader(data.disclaimer)}</div>
    )}
    {animate && !stream.done && (
      <button type="button" className="stream-skip is-inline" onClick={stream.skip}>
        显示全部
      </button>
    )}
  </>
);

/* ---------- 解读模式（八小节） ---------- */
const ReadingBubble: React.FC<{ data: BaziReadingData; visibleSections: Array<[string, string | undefined]>; animate: boolean }> = ({ data, visibleSections, animate }) => {
  const mainTexts = visibleSections.map(([, value]) => stripHeader(value || ''));
  const summaryText = data.summary ? stripHeader(data.summary) : '';
  const streamTexts = summaryText ? [...mainTexts, summaryText] : mainTexts;
  const summaryIndex = summaryText ? streamTexts.length - 1 : -1;
  const stream = useStreamedText(streamTexts, animate);
  const { parts, active } = sliceStream(streamTexts, stream.visible);

  return (
    <div className={`bz-ai-bubble ${animate && !stream.done ? 'is-streaming' : ''}`}>
      <div className="bz-ai-reading">
        {visibleSections.map(([name], index) => (
          <div key={name} className="bz-ai-block">
            <b className="bz-ai-block-header">{`【${name}】`}</b>
            <BaziTextLines
              text={parts[index] || ''}
              caret={animate && !stream.done && active === index}
            />
          </div>
        ))}
      </div>
      {summaryText && (
        <div className="bz-ai-bubble-takeaway">
          综括：
          {parts[summaryIndex] || ''}
          {animate && !stream.done && active === summaryIndex && (
            <span className="stream-caret" aria-hidden />
          )}
        </div>
      )}
      <CommonTail data={data} stream={stream} animate={animate} />
    </div>
  );
};

/* ---------- 问答新模式（师傅式结构化：直答 → 白话现状 → 做/避 → 应期 → 折叠依据） ---------- */
const StructuredAskBubble: React.FC<{ data: BaziReadingData; animate: boolean }> = ({ data, animate }) => {
  const clean = (s: any) => stripHeader(String(s || ''));
  const headline = clean(data.answer);
  const situation = clean(data.situation);
  const advice = (Array.isArray(data.advice) ? data.advice : []).map(clean).filter(Boolean);
  const timing = clean(data.timing);
  const reason = clean(data.reason);

  const blocks: Array<{ kind: 'headline' | 'situation' | 'advice' | 'timing'; text: string }> = [];
  if (headline) blocks.push({ kind: 'headline', text: headline });
  if (situation) blocks.push({ kind: 'situation', text: situation });
  advice.forEach(t => blocks.push({ kind: 'advice', text: t }));
  if (timing) blocks.push({ kind: 'timing', text: timing });

  const streamTexts = blocks.map(b => b.text);
  const stream = useStreamedText(streamTexts, animate);
  const { parts, active } = sliceStream(streamTexts, stream.visible);

  return (
    <div className={`bz-ai-bubble ${animate && !stream.done ? 'is-streaming' : ''}`}>
      {data.domain && <span className="bz-ai-domain">{data.domain}</span>}
      {blocks.map((b, k) => {
        const tail = animate && !stream.done && active === k ? <span className="stream-caret" aria-hidden /> : null;
        if (b.kind === 'headline') {
          return <div key={k} className="bz-ai-answer">{parts[k] || ''}{tail}</div>;
        }
        if (b.kind === 'situation') {
          return <p key={k} className="bz-ai-situation">{parts[k] || ''}{tail}</p>;
        }
        if (b.kind === 'advice') {
          const full = splitAdviceTag(b.text);
          const shown = parts[k] || '';
          const body = full.tag
            ? shown.replace(new RegExp(`^${full.tag}[:：]\\s*`), '')
            : shown;
          const avoid = !!full.tag && '避忌禁'.includes(full.tag);
          return (
            <div key={k} className="bz-ai-advice">
              {full.tag && (
                <span className={`bz-ai-advice-tag${avoid ? ' is-avoid' : ''}`}>{full.tag}</span>
              )}
              <span className="bz-ai-advice-text">{body}{tail}</span>
            </div>
          );
        }
        return (
          <div key={k} className="bz-ai-timing">
            <span className="bz-ai-timing-tag">何时</span>
            <span>{parts[k] || ''}{tail}</span>
          </div>
        );
      })}
      {stream.done && reason && (
        <details className="bz-ai-evidence">
          <summary>盘面依据</summary>
          <p>{reason}</p>
        </details>
      )}
      <CommonTail data={data} stream={stream} animate={animate} />
    </div>
  );
};

/* ---------- 问答旧版（历史会话里的整段回答，保持原渲染） ---------- */
const LegacyAskBubble: React.FC<{ data: BaziReadingData; animate: boolean }> = ({ data, animate }) => {
  const summaryText = data.summary ? stripHeader(data.summary) : '';
  const streamTexts = [data.analysis || '', summaryText].filter(Boolean) as string[];
  const summaryIndex = summaryText ? streamTexts.length - 1 : -1;
  const stream = useStreamedText(streamTexts, animate);
  const { parts, active } = sliceStream(streamTexts, stream.visible);
  return (
    <div className={`bz-ai-bubble ${animate && !stream.done ? 'is-streaming' : ''}`}>
      <div className="bz-ai-ask-reply">
        <BaziTextLines
          text={parts[0] || ''}
          caret={animate && !stream.done && active === 0}
        />
      </div>
      {summaryText && (
        <div className="bz-ai-bubble-takeaway">
          结论：{parts[summaryIndex] || ''}
          {animate && !stream.done && active === summaryIndex && (
            <span className="stream-caret" aria-hidden />
          )}
        </div>
      )}
      <CommonTail data={data} stream={stream} animate={animate} />
    </div>
  );
};

/* ---------- 单条消息渲染（聊天面板与历史详情共用） ---------- */
export const BaziAiMessageView: React.FC<{ msg: BaziChatMessage; animate?: boolean }> = ({ msg, animate = false }) => {
  if (msg.role === 'user') {
    return (
      <div className="bz-ai-msg bz-ai-msg-user">
        <div className="bz-ai-bubble">{typeof msg.content === 'string' ? msg.content : ''}</div>
      </div>
    );
  }
  return <BaziAssistantMessage msg={msg} animate={animate} />;
};

const BaziAssistantMessage: React.FC<{ msg: BaziChatMessage; animate: boolean }> = ({ msg, animate }) => {
  const data = (typeof msg.content === 'object' && msg.content ? msg.content : {}) as BaziReadingData;
  const sections: Array<[string, string | undefined]> = [
    ['人生总论', data.overview],
    ['性格特质', data.personality],
    ['事业官禄', data.career],
    ['财运', data.wealth],
    ['婚姻感情', data.love],
    ['健康', data.health],
    ['家庭六亲', data.family],
    ['大运提示', data.luckCycle]
  ];
  const visibleSections = sections.filter(([, v]) => typeof v === 'string' && v.length > 0);
  const reading = visibleSections.length > 0;
  const structuredAsk = !!(data.answer || data.situation || (Array.isArray(data.advice) && data.advice.length > 0));

  if (typeof msg.error === 'string' && msg.error) {
    return (
      <div className="bz-ai-msg bz-ai-msg-bot">
        <div className="bz-ai-bubble bz-ai-error">⚠ {msg.error}</div>
      </div>
    );
  }

  return (
    <div className="bz-ai-msg bz-ai-msg-bot">
      {reading ? (
        <ReadingBubble data={data} visibleSections={visibleSections} animate={animate} />
      ) : structuredAsk ? (
        <StructuredAskBubble data={data} animate={animate} />
      ) : (
        <LegacyAskBubble data={data} animate={animate} />
      )}
    </div>
  );
};

/* ---------- 消息列表（供历史详情复用） ---------- */
export const BaziAiThreadView: React.FC<{
  messages: BaziChatMessage[];
  threadRef?: React.MutableRefObject<HTMLDivElement | null>;
  /** Timestamp of the assistant message that should stream in. */
  streamTs?: number | null;
}> = ({ messages, threadRef, streamTs = null }) => (
  <div className="bz-ai-thread" ref={threadRef}>
    {messages.length === 0 && <div className="bz-ai-empty">暂无对话。</div>}
    {messages.map((m, i) => (
      <BaziAiMessageView
        key={i}
        msg={m}
        animate={m.role === 'assistant' && m.ts === streamTs}
      />
    ))}
  </div>
);

/* ============================================================ */

export default function BaziAiChat({ chart }: { chart: any }) {
  const { user } = useAuth();
  const identity = useMemo(() => chartIdentity(chart), [chart]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionTitle, setSessionTitle] = useState('');
  const [messages, setMessages] = useState<BaziChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [sendError, setSendError] = useState('');
  const [restoredNote, setRestoredNote] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [streamTs, setStreamTs] = useState<number | null>(null);
  const threadRef = useRef<HTMLDivElement | null>(null);
  const identityRef = useRef('');
  const cacheTimer = useRef<number | null>(null);
  const userInteractedRef = useRef(false);

  // 挂载 / 命盘身份变化：从本地缓存恢复该命盘的会话（不丢上下文）
  useEffect(() => {
    if (!identity) return;
    identityRef.current = identity;
    const hit = loadAllCache().find(e => e.identity === identity);
    setSessionId(hit?.sessionId ?? null);
    setSessionTitle(hit?.title ?? '');
    setMessages(hit?.messages ?? []);
    setStreamTs(null);
    if (hit && hit.messages.length > 0) setRestoredNote(true);
  }, [identity]);

  // debounce 固化缓存
  useEffect(() => {
    if (!identity || messages.length === 0) return;
    if (cacheTimer.current) window.clearTimeout(cacheTimer.current);
    cacheTimer.current = window.setTimeout(() => {
      saveCache({ identity, sessionId, title: sessionTitle, messages, updatedAt: Date.now() });
    }, 400);
    return () => {
      if (cacheTimer.current) window.clearTimeout(cacheTimer.current);
    };
  }, [identity, sessionId, sessionTitle, messages]);

  // 只有用户主动发起对话后才滚到聊天区；初次进入/恢复缓存时不抢盘面的滚动位置。
  useEffect(() => {
    if (!userInteractedRef.current) return;
    if (!threadRef.current) return;
    requestAnimationFrame(() => {
      if (threadRef.current) {
        smoothScrollToElement(threadRef.current, { align: 'end', padding: 24, duration: 520 });
      }
    });
  }, [messages.length, busy]);

  const runAsk = useCallback(async (mode: 'reading' | 'ask', question: string) => {
    if (busy || !chart || !identity) return;
    userInteractedRef.current = true;
    const shownText = mode === 'reading' ? '【大师解读】请按八个小节解读我的八字。' : question;
    setBusy(true);
    setSendError('');
    setRestoredNote(false);
    const userMsg: BaziChatMessage = { role: 'user', content: shownText, mode, ts: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    try {
      const res = await askBazi(user, {
        mode,
        question: mode === 'ask' ? question : undefined,
        chart,
        history: messages, // 服务端会把本轮 user/assistant 追加进会话
        sessionId
      });
      const ts = Date.now();
      const assistantMsg: BaziChatMessage = { role: 'assistant', content: res.data, mode, ts };
      setMessages(prev => [...prev, assistantMsg]);
      setStreamTs(ts);
      if (res.sessionId) setSessionId(res.sessionId);
      if (res.title) setSessionTitle(res.title);
    } catch (err: any) {
      const status = err?.status;
      let text: string;
      if (status === 400) text = `请求被拒：${err?.message || '输入内容未通过服务端校验'}`;
      else if (status === 401 || status === 403) text = '登录状态已过期，请刷新页面后重试。';
      else if (status === 500) text = err?.message || '解读服务还没配置模型或暂时出错，请稍后再试。';
      else if (status === 502) text = 'AI 服务返回了无效内容，请重试。';
      else if (status === 429) text = '请求太快了，休息一下再发。';
      else text = '解读服务器暂时无响应，请检查网络后重试。';
      setMessages(prev => [...prev, { role: 'assistant', content: '', mode, error: text, ts: Date.now() }]);
      setSendError(text);
    } finally {
      setBusy(false);
    }
  }, [busy, chart, identity, messages, sessionId, user]);

  const submitQuestion = () => {
    const q = input.trim();
    if (!q || busy) return;
    setInput('');
    void runAsk('ask', q);
  };

  const resetSession = () => {
    if (!confirmReset) {
      setConfirmReset(true);
      window.setTimeout(() => setConfirmReset(false), 4000);
      return;
    }
    setConfirmReset(false);
    setMessages([]);
    setSessionId(null);
    setSessionTitle('');
    setSendError('');
    setRestoredNote(false);
    setStreamTs(null);
    if (identity) saveCache({ identity, sessionId: null, title: '', messages: [], updatedAt: Date.now() });
  };

  if (!chart) return null;

  const hasMessages = messages.length > 0;
  const inputDisabled = busy;

  return (
    <section className="bz-ai-section" id="bazi-ai">
      <div className="bz-ai-head">
        <h2 className="bz-ai-title">AI 求测与解读</h2>
        <p className="bz-ai-sub">
          八字师傅按子平法批命：八小节完整解读人生总论、性格、事业、财运、婚姻、健康、六亲与大运；也可以只问一个问题——师傅先给一句直答，再说人话，落到「做／避」与具体时机。
        </p>
      </div>

      {restoredNote && hasMessages && (
        <div className="bz-ai-restored">已恢复此命盘的求测记录，可继续追问。</div>
      )}

      <BaziAiThreadView messages={messages} threadRef={threadRef} streamTs={streamTs} />

      {busy && (
        <div className="bz-ai-msg bz-ai-msg-bot bz-ai-msg-typing">
          <div className="bz-ai-typing"><i /><i /><i /></div>
        </div>
      )}

      {sendError && !hasMessages && (
        <div className="bz-ai-msg bz-ai-msg-bot">
          <div className="bz-ai-bubble bz-ai-error">
            ⚠ {sendError}
            {sendError.includes('重试') && (
              <button type="button" className="bz-ai-retry" onClick={() => setSendError('')}>关闭</button>
            )}
          </div>
        </div>
      )}

      {/* 输入区：无对话时给主按钮＋建议问题；有对话后收敛为单一追问条 */}
      {!busy && (
        <div className="bz-ai-composer">
          {!hasMessages && (
            <>
              <div className="bz-ai-entry">
                <button type="button" className="bz-ai-btn-primary" onClick={() => void runAsk('reading', '')}>
                  大师解读
                </button>
              </div>
              <div className="bz-ai-hint">
                {READING_HINTS.map(hint => (
                  <button key={hint} type="button" className="bz-ai-hint-chip" onClick={() => setInput(hint)}>
                    {hint}
                  </button>
                ))}
              </div>
            </>
          )}
          <div className="bz-ai-ask">
            <textarea
              className="bz-ai-input"
              value={input}
              rows={hasMessages ? 1 : 2}
              maxLength={500}
              placeholder={hasMessages ? '继续追问…（同一命盘）' : '想问什么？比如：我的事业适合什么方向？'}
              disabled={inputDisabled}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  submitQuestion();
                }
              }}
            />
            <button type="button" className="bz-ai-send" onClick={submitQuestion} disabled={!input.trim() || busy}>
              发送
            </button>
          </div>
          {hasMessages && (
            <div className="bz-ai-footer">
              <button type="button" className="bz-ai-reset" onClick={resetSession}>
                {confirmReset ? '再点一次确认清空' : '新会话'}
              </button>
              {sessionTitle && <span className="bz-ai-session-title">{sessionTitle}</span>}
            </div>
          )}
        </div>
      )}

      <p className="bz-ai-disclaimer">
        分析内容为传统命理文化的辅助推导，仅供参考，不构成医疗、投资或法律建议；身体与重大决策请以专业意见为准。
      </p>
    </section>
  );
}
